import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import FuseUtils from '@fuse/utils';
import { authPermissions } from 'app/auth';
import InvoicePaymentsPanel from 'app/main/dashboards/main/dialogs/InvoicePaymentsPanel';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Confirm from '@fuse/core/FuseDialog/confirm';
import { openDialog } from 'app/store/fuse/dialogSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Tooltip, CircularProgress, Backdrop, Portal } from '@mui/material';
import _, { capitalize, lowerCase, upperCase } from 'lodash';
import dispatchMessage, { checkNullOrUndefined, isCustomer, isSupplier, isVasTeam } from 'app/main/apps/common/AuraFunctions';
import ProductImagesLink, { productImagesPath } from './ProductImagesLink';
import { getApiErrorMessage } from 'app/utils/apiError';
import MenuActions from 'app/main/dashboards/main/dialogs/MenuActions';
import { selectActions, selectUser } from 'app/store/userSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useFormContext } from 'react-hook-form';
import { getOrders, openReallocationDialog, deleteShipment, getStoneOrder } from '../store/ordersSlice';
import {
  openOrderDialog,
  openViewOrderDialog,
  openChooseSupplierDialog,
  convertToOrder,
  updateOrder,
  getQuotedSuppliers,
  openSupplierShippingDialog,
  closeViewOrderDialog,
  performAction,
  getOrderDetails,
  downloadOrderPdf,
  closeOrderDialog,
  openConfirmInvoiceDialog,
  getAdvancePayment,
  getBriefInvoiceDetails,
  sendEmail,
  getComments,
  showDialog,
  sendReminderEmail,
} from '../store/orderSlice';
import { getEcomWidgetsOrderItems, getShipmentInfo, viewNote, viewNotes } from '../../../dashboards/main/store/widgetsSlice';
import { rejectRfqQuote } from '../store/rfqSlice';
import { getSentToSuppliers, getSuppliers } from '../store/rfqsSlice';
import { openProductDialog } from '../store/productSlice';
import downloadFile from '../../common/AuraDownload';
import { getShipments } from '../../backoffice/store/shipmentsSlice';
import { getShipmentInformation } from '../../backoffice/store/shipmentSlice';
import { getOpenContainers, updateShipment, updateShipmentContainer, viewShipment } from '../../backoffice/store/shipmentOrderSlice';
import {
  getActionStage,
  getActionRouteBase,
  INPRODUCTION_ORDERS_STAGE,
  SHIPPED_ORDERS_VIEW_FILTER,
} from './actionStage';
import showConfirmationDialog from '../../common/AuraSimpleConfirmDialogs';
import { changeContainerStatusOnce } from './containerStatusChange';
import { isArchivedView } from '../../common/archivedRecord';
import { archivedInvoiceReversalAction } from './archivedInvoiceReversal';
import {
  buildInlineDocumentsPayload,
  firstClickPopupAction,
  isViewAction,
  opensApprovalDialogFromPopup,
  splitPopupActions,
} from '../../common/popupActions';
import { withoutPackingSlipWhenNoShipment } from './packingSlipAction';

function OrdersRowActions({ row, cell, closeDialog, popupButtons, approveProof, approveNote, listData, selectedImages, popupData, viewFilter, onReload }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const allaActions = useSelector(selectActions);
  const user = useSelector(selectUser);
  const locationSearchKey = window.location.search;
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(locationSearchKey);
  const sidValue = urlParams.get('sid');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // PGSS-5471: a first-click popup action is running -- its buttons are disabled so a second click cannot send twice.
  const [popupActionRunning, setPopupActionRunning] = useState(false);
  // PGSS-5544: the invoice whose payments are being reversed. Held here rather than in a shared
  // dialog so this stays inside the invoices list and touches nothing the order screens use.
  const [reversePaymentFor, setReversePaymentFor] = useState(null);

  const buildSelectedDrawing = (selectedImagesData, fullDetails) => {
    if (fullDetails?.length === 0) return []
    return fullDetails.map((detail, index) => {
      const selectedDocs = selectedImagesData[index] || [];
      const selectedDocIds = selectedDocs.map(doc => doc.id);

      const filteredDocuments = detail?.drawing_documents?.filter(doc => selectedDocIds.includes(doc.id));

      return {
        ...detail,
        vas: detail?.vas ? JSON.stringify(detail.vas) : null,
        drawing_documents: filteredDocuments
      };
    });
  };

  const methods = useFormContext()
  const { setError } = methods || {}

  const onCellClick = (ev) => {
    ev.stopPropagation();
  };
  const { original } = row;

  const type = location.pathname.split('/')[1];
  const type2 = location.pathname.split('/')[2]

  const getStage = () => getActionStage(type, viewFilter);

  let stage = getStage()
  if (type?.includes('awaiting')) stage = 'order'
  const stageAction = allaActions.filter((action) => action.stage === stage);
  let actions = stageAction.find((action) =>
    (action?.fromStatusesTypeCode === original?.status?.system_code) && action?.fromStatusesTypeCode)?.actionList[0];
  if (actions) actions = JSON.parse(actions);

  // PGSS-5446: an archived record carries NO configured actions. Clearing them here -- rather than
  // suppressing the render further down -- is what makes View Order appear.
  //
  // Suppressing the render alone left the row completely EMPTY on any status that has a
  // workflow_actions row, because View Order is only ever produced by the `!actions` default
  // branch below. It looked correct in review because an archived order usually sits at a terminal
  // status (Completed & Paid in Full) which has no configured row -- so `actions` was already empty
  // and View Order showed by accident, on exactly the statuses anyone would spot-check.
  const isArchived = isArchivedView(locationSearchKey);
  // PGSS-5551: captured BEFORE the clear below, which is what stopped a paid-in-full invoice from
  // ever offering the reversal PGSS-5544 configured for it. Invoices only -- see the helper.
  const archivedInvoiceReversal = archivedInvoiceReversalAction({ isArchived, stage, type, actions });
  if (isArchived) actions = undefined;
  // PGSS-5544 / PGSS-5306: supplier invoices share the invoice stage -- getActionStage maps
  // 'supplier-invoices' to 'invoice' -- so they inherit the staff invoice action list wholesale.
  // Reverse Payment and Review discrepancy are customer-invoice only. Matched on ui_id, not icon.
  if (type === 'supplier-invoices') {
    actions = actions?.filter((el) => !(['cancel', 'visibility', 'alarm'].includes(el.icon))
      && el?.ui_id !== 'REVERSE_PAYMENT'
      && el?.ui_id !== 'REVIEW_INVOICE_DISCREPANCY');
  }

  const isOutboundShipments = ['outbound', 'order-shipments'].includes(type);

  if (row?.original?.is_stone_order && !row?.original?.is_proof_approval && actions) {
    actions = actions.filter(el => el.name !== 'Create Supplier Order')
  }

  if (!row?.original?.is_stone_order && row?.original?.is_proof_approval && actions) {
    actions = actions.filter(el => el.name !== 'Create Stone Order')
  }

  if (!row.original.is_send_to_supplier_for_proof && actions) {
    actions = actions.filter(el => el.name !== 'Send to Supplier for Proof')
  }

  if (sidValue === "all" && actions) {
    actions = actions.filter(el => ['View Order', 'Receive Order'].includes(el.name))
  }

  // PGSS-5829: no Packing Slip on a row with no shipment, rather than /shipments/print-label/undefined.
  actions = withoutPackingSlipWhenNoShipment(actions, row?.original)

  if (stage === "order" && (row.original.order_type === "DS" || row.original.order_type === "Direct Shipment" || row.original.order_type === "DP" || row.original.order_type === "Direct Pickup") && actions) {
    actions = actions.filter(el => el.name !== 'Receive Order')
  }

  const showRejectQuote = stage === 'quote' && popupButtons && popupData && popupData?.name === "Review Quote" && cell?.column.userManage;
  const isReviewActions = stage === 'order' && popupButtons && popupData && (popupData?.parentParam?.uiId === "REVIEW_DRAWING_ORDER" || popupData?.parentParam?.uiId === "REVIEW_DRAWING") && cell?.column.userManage;

  // PGSS-5453: `count`/`showMore` are gone. They were the popup's cap and overflow trigger, and
  // they counted a different list than the one the overflow menu indexed into -- see
  // popupActions.js. maxCount stays: it is the inline limit, now applied by splitPopupActions.
  const maxCount = showRejectQuote ? 4 : 3;
  let key = stage === 'invoice' ? 'invoice_id' : 'oid';
  if(isOutboundShipments === true) key = 'shipment_id';
  let getTitle;

  if (['in-production', 'fulfilled', 'confirmed','container-management', INPRODUCTION_ORDERS_STAGE].includes(stage)) {
    // PGSS-5369: the In Production Orders View lists orders, so its default action is View Order.
    // Without this the ui_id became VIEW_INPRODUCTION-ORDERS, which is not in allowActions, and
    // the button rendered with no link.
    getTitle = 'order';
  } else if (stage === 'completed') {
    getTitle = 'rfd';
  } else {
    getTitle = stage;
  }
  const allowActions = [
    'EDIT_ORDER',
    'EDIT_QUOTE',
    'VIEW_ORDER',
    'VIEW_QUOTE',
    'CONVERT_TO_QUOTE',
    'CONVERT_TO_PROOF',
    'EDIT_RFQ',
    'VIEW_RFQ',
    'EDIT',
    'SHIPMENT_PRINT',
    'EDIT_SHIPMENT',
    'PRINT_PRODUCTION',
    'STATUS_UPDATE',
    'REVISE_QUOTE',
    'REVIEW_NOTE',
    'SHIPMENT_COMMUNICATION',
    'VIEW_SUPPLIER_INVOICE',
    'RECEIVE_SHIPMENT',
    'PACKING_SLIP',
    'SUBMIT_VAS_PROOF',
    'VAS_PROOF_INPROGRESS',
    'REQUEST_INFORMATION',
    'REVIEW_PROOF',
    'READY_FOR_PLOTTING',
    'IN_SANDBLASTING',
    'IN_LASER_ETCHING',
    'READY_SEND_FOR_VAS_APPROVAL',
    'REVIEW_VAS',
    'ATTACH_DRAWING',
    'UPLOAD_IMAGE',
    'APPROVE_PRODUCT_IMAGE',
    'REVISE_PRODUCT_IMAGE',
    'EDIT_SUPPLIER_QUOTE',
    'LOAD_ORDERS',
  ];

  const basedOnImageCustomzation = () => {
    let Key = ''
    if (original.image_customisation) {
      Key = 'update_status_with_attachment'
    }
    else {
      Key = 'updateStatus'
    }
    return Key
  }

  const refreshTableData = (id) => {
    // PGSS-5825: a staff list hands in its own load, so the reload asks for exactly what the page showed (supplier
    // invoices, its tab and scope), not a request rebuilt here that drops them.
    if (onReload) {
      onReload();
      return;
    }
    if (['outbound', 'order-shipments'].includes(type)) {
      dispatch(getShipments({}))
    }

    let params = {}
    if (type2 === 'containers')
      params = { stage: 'container' }
    else if (type?.includes('awaiting'))
      params = { title: 'proof' }
    else
      params = { stage: id === 'STATUS_UPDATE' && stage === 'quote' ? 'order' : stage }
    // PGSS-5825: without it the reload returns customer invoices into Supplier Invoices.
    if (type === 'supplier-invoices') params.is_supplier = true

    dispatch(getOrders({ params }))
  };

  const confirmation = (toStatusCode, id, otherPayload = {}, widgetData, code, approvalPayload) => {
    if(id === "SEND_ORDER_FOR_DRAWING_APPROVAL") {
      // Disable the SEND_ORDER_FOR_DRAWING_APPROVAL button once it is clicked
      // Currently disable button is handled only for SEND_ORDER_FOR_DRAWING_APPROVAL
      setIsSubmitted(true)
    }
    let action;
    const orderDetails = [];
    const isSupplierRejectQuote = id === 'REJECT_QUOTE' && isSupplier(user.role);
    const approveQuoteSuccessMessageForCustomer = id === 'STATUS_UPDATE' && isCustomer(user.role) ? 'Quote has been Approved Successfully' : ''
    const performApi = ['MAKE_IN_PROGRESS', 'CANCEL_INVOICE', 'SEND_REMINDER', 'RFQ_REMINDER', 'UPLOAD_DOCUMENT'].includes(id)
    const isApproval = ['REJECT_DRAWING', 'REQUEST_REVISION_FOR_DRAWING', 'APPROVE_DRAWING', 'APPROVE_DRAWING_ORDER',
      'SEND_FOR_DRAWING_APPROVAL', 'SEND_ORDER_FOR_DRAWING_APPROVAL', 'SEND_FOR_PROOF_APPROVAL', 'REQUEST_REVISION_FOR_PROOF', 'APPROVE_PROOF', 'REJECT', 'APPROVE_VAS_PRICE', 'SEND_PRICE_APPROVAL_FOR_VAS', 'MAKE_SHIPMENT', 'REQUEST_REVISION_FOR_VAS_PRICE'].includes(id)
    const isAttachmentPayload = ['SEND_FOR_DRAWING_APPROVAL', 'SEND_FOR_PROOF_APPROVAL'].includes(id)
    if(isSupplierRejectQuote) {
      action = dispatch(
        rejectRfqQuote({
          id: original.id,
          note: otherPayload?.note
        })
      )
    }
    else if (isAttachmentPayload) {
      otherPayload?.order_details?.forEach((item, index) => {
        orderDetails.push({
          id: widgetData[index]?.id,
          vas: id === 'SEND_FOR_PROOF_APPROVAL' ? JSON.stringify(widgetData[index]?.vas) : item.vas,
          ...item
        })
      })
      action = dispatch(
        updateOrder({
          id: original.id,
          to_statuses_type_id: code || toStatusCode,
          [isApproval ? 'update_status_with_attachment' : basedOnImageCustomzation()]: 'yes',
          note: otherPayload?.note,
          order_details: orderDetails
        })
      )
    }
    else if (performApi)
      action = dispatch(performAction({
        ui_id: id,
        data: {
          [id === 'UPLOAD_DOCUMENT' ? 'shipment_id' : 'id']: id === 'UPLOAD_DOCUMENT' ? original.shipment_id : original.id,
          ...(id === 'UPLOAD_DOCUMENT' && { orders: [...approvalPayload] })
        }
      }));
    else
      action = dispatch(
        updateOrder({
          id: original.id,
          to_statuses_type_id: code || toStatusCode,
          [isApproval ? 'update_status_with_attachment' : basedOnImageCustomzation()]: 'yes',
          ...otherPayload
        })
      )

    // PGSS-5471: returned so a first-click popup action can re-enable its buttons when the request ends.
    return action.then((res) => {
      if(id === "SEND_ORDER_FOR_DRAWING_APPROVAL") {
        setIsSubmitted(false)
      }
      if (!res.error) {
        dispatch(showMessage({ type: 'success', message: approveQuoteSuccessMessageForCustomer || 'Success' }));
        refreshTableData(id)
        dispatch(closeViewOrderDialog());
        if (id === 'STATUS_UPDATE' && stage === 'quote') navigate("/orders")
      } else {
        // PGSS-5613: a rejected thunk carries the backend's error body in `payload`, not on the action itself.
        throw new Error(getApiErrorMessage({ response: { data: res.payload ?? res } }, 'Failed to update'));
      }
    }).catch((error) => {
      dispatch(showMessage({ type: 'error', message: error.message }));
      if(id === "SEND_ORDER_FOR_DRAWING_APPROVAL") {
        setIsSubmitted(false)
      }
    });
  };

  const checkData = (data) => {
    let validProduct = true;
    data.every((product) => {
      if (!product.color || !product.type || !product.length || !product.width || !product.height) {
        validProduct = false;
      }
      return null;
    })

    return validProduct;

  }

  // PGSS-5369 / PGSS-5367: supplier Orders Views where an action must appear even when the row's
  // status has no workflow_actions row. `defaultAction` is rendered behind a `!isSupplier` gate
  // unless this flag is set — without it, Send Message is invisible to the role that needs it.
  //
  // PGSS-5367: Shipped Orders → Orders View. Statuses like Pending For Receive Shipment have no
  // configured actions on the fulfilled stage, so the cell was empty while Completed & Shipped
  // still showed icons. Messaging is not status-dependent (same rationale as PGSS-5369).
  const isSupplierShippedOrdersView =
    isSupplier(user.role) && stage === 'fulfilled' && viewFilter === SHIPPED_ORDERS_VIEW_FILTER;
  const isSupplierOrdersView =
    isSupplier(user.role)
    && (stage === INPRODUCTION_ORDERS_STAGE || isSupplierShippedOrdersView);

  const sendMessageAction = {
    dashboardAction: true,
    icon: "rate_review",
    name: "Send Message",
    to_statuses_types_code: "",
    to_statuses_types_id: "",
    ui_id: "REVIEW_NOTE",
  };

  const defaultAction = [];

  // View Order is deliberately NOT given to a supplier here. WorkflowActionsHelper strips
  // VIEW_ORDER, VIEW_RFQ and VIEW_QUOTE from every supplier and customer action set on the server;
  // rendering it from the client would quietly undo that decision.
  if (!isSupplierOrdersView) {
    defaultAction.push({
      dashboardAction: true,
      icon: "preview",
      name: `View ${capitalize(getTitle)}`,
      to_statuses_types_code: "",
      to_statuses_types_id: "",
      ui_id: `VIEW_${upperCase(getTitle)}`,
    });
  }

  // Send Message is rendered directly rather than configured per status. Messaging is not
  // status-dependent the way a workflow action is: "Mark as Completed and Shipped" makes sense at
  // one point in an order's life, "send a message about this order" makes sense at every point.
  // Driving it from workflow_actions means enumerating statuses, and the icon then appears on one
  // row and not the row beneath it -- the complaint that opened this ticket.
  //
  // The statuses here make that concrete: six are order_status, but SHIPMENT_CONFIRMATION_PENDING
  // is a shipment_status carrying ~500 orders. Configuring against it means seeding a shipment
  // status for a list of orders; excluding it drops those orders. Neither is right, and the
  // question disappears once the icon does not depend on status at all.
  // `!isArchived` for the same reason as above: an archived record is viewable, not actionable,
  // and the ticket asks for View Order and nothing else.
  if ((stage === INPRODUCTION_ORDERS_STAGE || isSupplierShippedOrdersView) && !isArchived) {
    defaultAction.push(sendMessageAction);
  }

  const paramForSupplier = ['art/design', 'stone']
  const workFlowAction = ['Send to Supplier for Proof', 'Send to Supplier']

  const getApprovalResetData = (data, showDocuments = false) => {
    const resetData = { order_details: [] }
    data?.forEach((item) => {
      resetData.order_details.push(
        {
          id: item.id,
          vas: item.vas,
          showDocuments
        }
      )
    })
    return resetData
  }

  const exportShipment = (isPrice) => {
    return (
      downloadFile({ params: { id: row.original.shipment_id, is_price: isPrice }, endPoint: '/api/exportShipment' })
        .then(() => {
          dispatch(showMessage({ type: 'success', message: 'File downloaded successfully' }))
          dispatch(closeOrderDialog({ field: 'orderDialog', }))
        })
        .catch(() => dispatch(showMessage({ type: 'error', message: 'Failed to download' })))

    )
  }

  const additionalButton = () => {
    return (
      <div className='flex gap-10'>
        <Button
          variant="contained"
          color="secondary"
          type="button"
          onClick={() => { exportShipment(true) }}
        >
          Export Shipment With Price
        </Button>
        <Button
          variant="contained"
          color="secondary"
          type="button"
          onClick={() => { exportShipment(false) }}
        >
          Export Shipment Without Price
        </Button>
      </div>
    )
  }

  const reviewOrder = (data, isEdittableView) => {
    const edittableData  = isEdittableView === true ? ["reference_no", "decedent", "comments"] : [];
    dispatch(
      openViewOrderDialog({
        completeOrderData: original,
        data: data.payload,
        param: {
          name: 'Review',
          type: 'View',
          title: `Review ${capitalize(getTitle)}`,
          showDialogActions: !cell.column.userManage ? true : null,
          loadOrdersFun: refreshTableData,
          edittableData,
          resetFormOrderDetails: isEdittableView,
          noDocumentsInForm: isEdittableView,
          passExtraPayload: isEdittableView,
          setUpdateOrderDetailsFlag: isEdittableView,
        },
        stage,
      }));
  }

  const handleClick = (id, toStatusCode, name, toStatusId) => {
    switch (id) {
      case 'APPROVAL_REMINDER':
        setLoading(true);
        dispatch(sendReminderEmail({id:getDetails().widgetId, params: {stage}})).then((sendReminderEmailResponse) => {
          if (!sendReminderEmailResponse?.error) {
            dispatch(showMessage({ type: 'success', message: 'Reminder email sent successfully' }));
          }
        })
        .finally(() => setLoading(false));
        break;
      case 'REQUEST_REVISION_SUPPLIER_DRAWING':
        setLoading(true);
        dispatch(getOrderDetails(({ id: original.id, params: {} })))
          .then(({ payload }) => {
            if (payload) {
              dispatch(
                openOrderDialog({
                  completeOrderData: original,
                  data: payload,
                  param: {
                    name: 'approveActions',
                    maxWidth: 'md',
                    showHeader: true,
                    showLatestRevisionNote: true,
                    chooseRevisionDocuments: true,
                    showUploadRevisionDocuments: true,
                    title: name,
                    successMessage: 'Revision Requested Successfully',
                    loadOrdersFun: refreshTableData,
                    data: {
                      uiId: id, toStatusCode
                    },
                    showAutoNote: true, // Set this field for schema creation and display AutoNote field section in NoteFormCOntent
                    noteFieldName: "revision_note", // Set this field for note field_name that will be sent to BE
                    noteErrorMessage: "Notes is Required", // Set this field for incase of error
                    noteFieldLabelName: "Notes", // Set this field for display Label name
                  },
                  stage
                })
              );
            }
            setLoading(false);
          }
        );
        break;
      case 'REVIEW_DRAWING':
      case 'REVIEW_DRAWING_ORDER':
        // REVIEW_DRAWING_ORDER -> Order drawing review to approve or request revision
        // REVIEW_DRAWING -> After supplier subitted drawing "Review Drawing" action will display in the table.
        // REVIEW_DRAWING -> Order drawing review to Send drawing for approval(customer approval), approve or request revision
        setLoading(true);
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name,
                    type: 'View',
                    title: name,
                    showDialogActions: true,
                    hideDialogActions: true,
                    uiId: id,
                    loadOrdersFun: refreshTableData,
                  },
                  stage,
                })
              );
            }
            setLoading(false);
          }
        );
        break;
      case 'INTERNAL_COMMENTS': {
          const commentsParam = { order_id: original?.id };
          const title = `${name} - ${_.capitalize(stage)} No: ${original.order_no}`
          if(original?.id) {
            setLoading(true);
            dispatch(getComments(commentsParam)).then((res) => {
              if (!res.error) {
                setLoading(false);
                dispatch(
                  openOrderDialog({
                    completeOrderData: original,
                    data: original,
                    param: {
                      name: 'notesHistory',
                      title,
                      maxWidth: 'md',
                      notesData: res?.payload || [],
                      noteFieldName: 'comments',
                      noteFieldLabelName:'Comments',
                      addNote: true,
                      showNoteHistory: true,
                      successMessage: 'Success',
                      submitButtonName: 'Submit',
                      purpose: "customerComments",
                    },
                    stage,
                  })
                );
              }
            });
          }
        }
        break;
      case 'STONE_ORDER_STATUS_CHECK':
        dispatch(getStoneOrder(original.id)).then((res) => {
          if (!res.error) {
            dispatch(
              openViewOrderDialog({
                completeOrderData: res?.payload,
                data: res?.payload.order_details,
                param: {
                  name,
                  type: 'View',
                  title: name,
                  hideAmountDetails: true,
                  showNameField: true,
                  showDialogActions: false,
                  hideCustomerName: true,
                },
                stage,
              })
            );
          } else if(res?.error && res?.error?.code === "ERR_BAD_REQUEST") {
            // Inventory order does not have the stone order. But currently we are displaying the Icon
            // SO, Added code here to show the message
            dispatchMessage(dispatch, 'Stone Order Record Not Found', 'info');
          }
        });
        break;
      case 'ADD_NOTE':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'note',
              title: 'Add Note',
              successMessage: 'Your note has been sent successfully',
            },
            stage
          })
        );
        break;
      case 'DELETE_SHIPMENT':
        dispatch(
          openDialog({
            children: (
              <Confirm title="Delete Shipment"
                content="Are you sure you want to permanently delete this item"
                onSubmit={() => {
                  setLoading(true);
                  dispatch(deleteShipment(
                    original.shipment_id,
                  )).then(res => {
                    setLoading(false);
                    if (!res.error) {
                      dispatch(showMessage({ message: "Selected item has been deleted successfully", type: 'success' }))
                      refreshTableData()
                    }
                  })
                }}
              />
            ),
          })
        );
        break;
      case 'APPROVE_SHIPMENT':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'approveActions',
              title: name,
              successMessage: 'Success',
              submitButtonName: 'Submit',
              loadOrdersFun: refreshTableData,
              isOutboundShipments: isOutboundShipments === true ? true : null,
              showCapacityWarning: original.capacity_exceeded,
              capacityExceedPercentage: original.capacity_exceed_percentage,
              keepDialogOpen:false // added this flag to have loader functionality on submit button
            },
            stage
          })
        );
        break;
      case 'SHIPMENT_READY_FOR_DISPATCH':
        dispatch(
          getShipmentInformation(original.shipment_id)
        ).then((resData) => {
          dispatch(
            openOrderDialog({
              completeOrderData: resData.payload,
              data: resData.payload,
              param: {
                name: 'approveActions',
                title: name,
                successMessage: 'Success',
                submitButtonName: 'Submit',
                loadOrdersFun: refreshTableData,
                isDispatch: isOutboundShipments === true,
                toStatusCode
              },
              stage
            })
          );
        });
      break;
      case 'CANCEL_ORDER':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'rejectQuote',
              title: name,
              submitButtonName: name,
              onSubmit: ({ note }) => {
                confirmation(toStatusCode, id, { note })
              }
            },
            stage
          })
        );
      break;
      case 'RECEIVE_SHIPMENT':
        /* dispatch(
          getEcomWidgetsOrderItems({
            shipment_id: original.shipment_id,
            stage: "in-production",
            invoiceId: true,
          })
        ).then((data) => {
          if (data && data.payload) {
            dispatch(
              openViewOrderDialog({
                completeOrderData: original,
                stage: "in-production",
                data: data.payload,
                param: {
                  name: "receive shipment",
                  type: "receive",
                  title: "Receive Shipment",
                  successMessage: 'Success',
                  submitButtonName: 'Receive',
                  showDialogActions: true,
                  is_vas_order: original?.is_vas_order,
                  packingSlips: original?.packing_slips || [],
                  supplierInvoices: original?.supplier_invoices || [],
                },
              })
            );
          }
        }); */
        break;
      case 'REJECT_SHIPMENT':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'approveActions',
              title: name,
              successMessage: 'Success',
              submitButtonName: 'Submit',
              loadOrdersFun: refreshTableData,
              keepDialogOpen:false
            },
            stage
          })
        );
        break;
      case 'DOWNLOAD':
        setLoading(true)
        dispatch(downloadOrderPdf(({ id: original.id, stage, orderNo: original?.order_no })))
          .finally(() => setLoading(false));
        break;
      case 'SEND_EMAIL':
        setLoading(true)
        dispatch(sendEmail(original.id)).then((sendEmailResponse) => {
          if (sendEmailResponse?.payload?.order) {
            dispatch(showMessage({ type: 'success', message: 'Email sent successfully' }));
          }
        })
        .finally(() => setLoading(false));
        break;
      case 'QUICK_EDIT':
        setLoading(true);
        dispatch(getOrderDetails(({ id: original.id, params: {} }))).then(({ payload }) => {
          dispatch(openOrderDialog({
            completeOrderData: original,
            data: payload,
            param: {
              name: 'edit_order',
              maxWidth : 'md',
              title: `Edit ${stage.replace(/^\w/, (c) => c.toUpperCase())} (Basic Info)`,
              successMessage: 'Success',
              submitButtonName: 'Save',
              loadOrdersFun: refreshTableData,
            },
            field: 'orderDialog',
            stage
          }))
        }).finally(() => {
          setLoading(false);
        })
        break;
      case 'CONFIRM_INVOICE':
        if (stage === 'shipment') {
          dispatch(
            openConfirmInvoiceDialog({
              data: { id: original?.shipment_id },
              param: {
                loadOrdersFun: refreshTableData,
              },
              stage
            })
          )
        } else {
          setLoading(true);
          dispatch(getBriefInvoiceDetails({ id: original.id }))
            .unwrap()
            .then((payloadData) => {
              dispatch(
                openOrderDialog({
                  completeOrderData: original,
                  data: { ...original, ...payloadData, id: original.id },
                  param: {
                    name: 'confirm_invoice',
                    title: 'Confirm Invoice',
                    maxWidth: payloadData?.discrepancy?.warning_active ? 'md' : 'sm',
                    successMessage: 'Invoice has been confirmed and sent to the customer',
                    loadOrdersFun: refreshTableData,
                  },
                  stage
                })
              );
            })
            .catch(() => {
              dispatchMessage(dispatch, 'Unable to load invoice details', 'error');
            })
            .finally(() => setLoading(false));
        }
        break;
      case 'CONFIRM_SHIPMENT':
        showConfirmationDialog(dispatch, {
          title: name,
          message: "Are you sure you want to confirm the shipment?",
          displayCancelBtn: 'yes',
          actionName: 'Confirm',
          onActionBtnClick: () => {
            const confirmParams = {
              id: original.shipment_id,
              confirm_invoice: true
            }
            dispatch(updateShipment(confirmParams)).then(res => {
            if (!res.error) {
              dispatch(showMessage({ message: "Shipment has been confirmed successfully", type: 'success' }))
              refreshTableData()
              }
            })
          }
        })
        break;
      case 'UPDATE_INVOICE':
        dispatch(getBriefInvoiceDetails({ id: original.id })).then((data) => {
          const payloadData = data.payload;
          payloadData.id = original.id;
          dispatch(
            openOrderDialog({
              completeOrderData: original,
              data: payloadData,
              param: {
                name: 'update_invoice',
                title: 'Update Invoice',
                maxWidth: payloadData?.discrepancy?.warning_active ? 'md' : 'sm',
                successMessage: 'Invoice has been updated and sent to the customer',
                loadOrdersFun: refreshTableData,
              },
              stage
            })
          )
        })
        break;
      case 'REVIEW_INVOICE_DISCREPANCY':
        // PGSS-5306: post-Invoiced entry for Accept / Flag / Raise — all order types, no tax edit.
        setLoading(true);
        dispatch(getBriefInvoiceDetails({ id: original.id }))
          .unwrap()
          .then((payloadData) => {
            dispatch(
              openOrderDialog({
                completeOrderData: original,
                data: { ...original, ...payloadData, id: original.id },
                param: {
                  name: 'review_invoice_discrepancy',
                  title: 'Review discrepancy',
                  maxWidth: payloadData?.discrepancy?.warning_active
                    || payloadData?.discrepancy?.latest_decision
                    ? 'md'
                    : 'sm',
                  hideDialogActions: true,
                  loadOrdersFun: refreshTableData,
                },
                stage,
              })
            );
          })
          .catch(() => {
            dispatchMessage(dispatch, 'Unable to load invoice details', 'error');
          })
          .finally(() => setLoading(false));
        break;
      case 'VIEW_NOTE':
        dispatch(viewNote({ id: original.id })).then((data) => {
          dispatch(
            openOrderDialog({
              completeOrderData: original,
              data: data.payload,
              param: {
                name: 'note',
                title: name,
              },
            })
          );
        })
        break;
      case 'VIEW_NOTES':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'view_notes',
              title: "View Notes",
              reqParam: {[getDetails().key]: getDetails().widgetId, stage: stage === 'container' ? 'in-production' : stage },
              maxWidth: 'md',
            },
          })
        );
          break;
      case 'INTERNAL_NOTES': {
        const details = getDetails();
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'internalNotes',
              title: `${_.startCase(_.toLower(name))} - ${capitalize(stage)} #${original?.order_no}`,
              reqParam: {[details.key]: details.widgetId, type: "internal" },
              maxWidth: 'lg',
              showDialogActions: false,
              hideDialogActions: true,
              showHeader: false,
              getNotesData: true,
            },
          })
        );
        break;
      }
      case 'VIEW_IN PRODUCTION':
      case 'VIEW_ORDER':
      case 'VIEW_QUOTE':
      case 'VIEW_RFQ':
      case 'VIEW_RFD':
      case 'VIEW_PAYMENT':
        if ((!cell.column.userManage && !isVasTeam(user.role)) || ['VIEW_PAYMENT'].includes(id))
          dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
            (data) => {
              if (data && data.payload) {
                // setOrderItems(data.payload);
                dispatch(
                  openViewOrderDialog({
                    completeOrderData: original,
                    data: data.payload,
                    param: {
                      name: id === 'VIEW_PAYMENT' ? 'payment' : 'viewOrder',
                      type: 'View',
                      title: id === 'VIEW_PAYMENT' ? 'Payment History' : `View ${capitalize(getTitle)} Items`,
                      isSupplierInvoice: type === 'supplier-invoices' || type === 'Supplier Invoice',
                      // showDialogActions: !cell.column.userManage ? true : null,
                      is_vas_order: original?.is_vas_order
                    },
                    stage
                  })
                );
              }
            }
          );
        break;
      case 'VIEW_SHIPMENT':
        dispatch(getEcomWidgetsOrderItems({ shipment_id: original.shipment_id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name,
                    type: 'View',
                    title: name,
                    showDialogActions: true,
                    loadOrdersFun: refreshTableData
                  },
                  stage,
                })
              );
            }
          }
        );
        break;
      case 'UPDATE_TRACKING':
        // PGSS-5407: fill in or correct the carrier / tracking (PRO) number on a shipment that has
        // already been dispatched. Read the shipment back first so the dialog opens on what was
        // captured at dispatch rather than on empty fields.
        dispatch(
          getShipmentInformation(original.shipment_id)
        ).then((resData) => {
          dispatch(
            openOrderDialog({
              completeOrderData: original,
              data: { ...resData.payload, shipment_id: original.shipment_id },
              param: {
                name: 'update_tracking',
                title: name,
                submitButtonName: 'Save',
                loadOrdersFun: refreshTableData,
              },
              stage
            })
          );
        });
        break;
      case 'SHIPMENT_EDIT':
        dispatch(
          getShipmentInformation(original.shipment_id)
        ).then((resData) => {
          dispatch(
            openOrderDialog({
              completeOrderData: resData.payload,
              data: resData.payload,
              param: {
                name: 'editShipment',
                title: name,
                successMessage: 'Success',
                submitButtonName: 'Submit',
                loadOrdersFun: refreshTableData,
                toStatusCode
              },
              stage
            })
          );
        });
        break;
      case 'REMOVE_SHIPMENT':
        showConfirmationDialog(dispatch, {
          title : name,
          message: "Are you sure you want to permanently delete this shipment?",
          displayCancelBtn: 'yes',
          actionName: 'Delete',
          onActionBtnClick: () => {
            const delShipParam = { id: original.shipment_id, to_statuses_type_id: toStatusCode }
            dispatch(updateShipment(delShipParam)).then(res => {
              if (!res.error) {
                dispatch(showMessage({ message: "Shipment has been deleted successfully", type: 'success' }))
                refreshTableData()
              }
            })
          }
        })
      break;
      case 'REVIEW_ORDER':
        setLoading(true);
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage }))
        .then((data) => {
          if(data && data.payload) {
            if(isCustomer(user.role) && (stage === "approval" || stage === "order")) {
              showConfirmationDialog(dispatch, {
                title: name,
                message: 'Do you want to update "Reference/Decedent" information?',
                displayCancelBtn: 'yes',
                actionName: 'Yes',
                cancelActionName: 'No',
                onActionBtnClick: (selectionType) => {
                  if(selectionType !== 'close') {
                    reviewOrder(data, true);
                  }else{
                    reviewOrder(data);
                  }
                },
              })
            } else {
              reviewOrder(data);
            }
          }
        })
        .finally(() => {
          setLoading(false);
        });
        break;
      case 'CONFIRM_ORDER':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: {
              id: original.id,
              action: 'status',
              status: 'Confirmed',
              order_no: row.original.order_no
            },
            param: {
              name: 'confirm',
              submitButtonName: 'Confirm Order',
              title: 'Confirm Order',
              successMessage: 'Order status has been updated successfully',
              loadOrdersFun: refreshTableData
            },
            stage
          })
        );
        break;
      case 'CONVERT_TO_ORDER':
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name,
                    type: 'approve',
                    submitButtonName: name,
                    title: name,
                    onSubmit: () => {
                      dispatch(convertToOrder({ id: original.id, to_status_id: toStatusId }))
                        .unwrap()
                        .then(refreshTableData)
                    }
                  },
                  stage
                })
              );
            }
          }
        );
        break;
      case 'RE_ALLOCATION':
        dispatch(
          openReallocationDialog({
            param: {
              name: 'reallocation',
              title: 'Re Allocation',
              submitButtonName: 'Submit',
              selectedFrom: original.id,
              loadOrdersFun: refreshTableData
            },
            stage
          })
        );
        break;
      case 'SEND_TO_SUPPLIER':
        setLoading(true);
        // Added for: "sent_to_supplier" param to differentiate View RFQ/Quote/Order Popup with SEND_TO_SUPPLIER popup
        // and to show only selected Revision documents from Revise Quote "Make it to In Review" popup
        dispatch(getEcomWidgetsOrderItems({ oid: original.id, stage, for: "sent_to_supplier" })).then(data => {
          if (checkData(data.payload)) {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    id: original.id,
                    name: 'send to supplier',
                    type: 'View',
                    title: name,
                    showDialogActions: false,
                    hideDialogActions: false,
                    submitButtonName: name,
                    action: name
                  },
                  stage,
                })
              );
            }
            // dispatch(
            //   openViewOrderDialog({
            //     completeOrderData: original,
            //     param: {
            //       id: original.id,
            //       name: 'send to supplier',
            //       type: 'View',
            //       title: name,
            //       maxWidth: 'md',
            //       submitButtonName: name,
            //       action: name
            //     },
            //     stage
            //   }))
          } else {
            dispatch(showMessage({
              message: 'Products related fields are missing Please fill in before sending to supplier',
              variant: 'error',
              autoHideDuration: null,
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            }))
          }
        })
          .finally(() => {
            setLoading(false);
          });

        if (workFlowAction.includes(name)) {
          dispatch(getSentToSuppliers(original.id));
          const index = workFlowAction.indexOf(name)
          const params = { type: paramForSupplier[index] }
          dispatch(getSuppliers({ suppliers: original.suppliers || [], params }));
        }
        break;
      case 'CHOOSE_SUPPLIER':
        dispatch(
          openChooseSupplierDialog({
            param: original.id,
            completeOrderData: original,
            name,
            status: original.status.name,
            stage,
            loadOrdersFun: refreshTableData,
          })
        );
        break;
      case 'VIEW_SHIPPING_INFO':
      case 'CREATE_SHIPPING':
        dispatch(
          openSupplierShippingDialog({
            param: original.id,
            completeOrderData: original,
            type: id === 'VIEW_SHIPPING_INFO' ? 'view' : 'create',
          })
        );
        break;
      case 'REJECT':
      case 'REJECT_DRAWING':
      case 'REJECT_DRAWING_ORDER':
      case 'REQUEST_REVISION_FOR_DRAWING':
      case 'REQUEST_REVISION_FOR_DRAWING_ORDER':
      case 'APPROVE_DRAWING':
      case 'APPROVE_DRAWING_ORDER':
      case 'RECEIVE_ORDER':
      case 'CANCEL_INVOICE':
      case 'SEND_REMINDER':
      case 'RFQ_REMINDER':
      case 'APPROVE_VAS_PRICE':
      case 'REQUEST_REVISION_FOR_VAS_PRICE':
        setLoading(true);
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name,
                    type: 'approve',
                    submitButtonName: name === 'Receive Order' ? 'Order Receive' : name,
                    title: name,
                    is_proof_approval: original.is_proof_approval,
                    onSubmit: (note) => confirmation(toStatusCode, id, { note }, data.payload),
                    actionName: id
                  },
                  stage
                })
              );
            }
          })
          .finally(() => {
            setLoading(false);
          }
          );
        break;
      case 'STATUS_UPDATE':
        if (stage !== 'rfq' && cell.column.userManage)
          dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
            (data) => {
              if (data && data.payload) {
                dispatch(
                  openViewOrderDialog({
                    completeOrderData: original,
                    data: data.payload,
                    param: {
                      name,
                      type: 'approve',
                      submitButtonName: name,
                      title: name,
                      is_proof_approval: original.is_proof_approval,
                      onSubmit: (note) => confirmation(toStatusCode, id, { note }, data.payload),
                    },
                    stage
                  })
                );
              }
            }
          );
        break;
      case 'REVIEW_QUOTE':
        setLoading(true);
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name: 'Review Quote',
                    type: 'View',
                    title: name,
                    showDialogActions: true,
                    hideDialogActions: true,
                  },
                  stage,
                })
              );
            }
            setLoading(false);
          }
        );
        break;
      case 'MAKE_IN_PROGRESS':
      case 'SEND_FOR_ORDER_APPROVAL':
      case 'SEND_FOR_PROOF_APPROVAL':
      case 'SEND_FOR_DRAWING_APPROVAL':
      case 'SEND_ORDER_FOR_DRAWING_APPROVAL':
        setLoading(true);
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name,
                    type: 'approve',
                    submitButtonName: name,
                    title: name,
                    is_proof_approval: original.is_proof_approval,
                    showAttachment: !['SEND_ORDER_FOR_DRAWING_APPROVAL'].includes(id),
                    showNameField: true,
                    showPriceField: !['SEND_FOR_PROOF_APPROVAL', 'MAKE_IN_PROGRESS'].includes(id),
                    sendFormValue: !['SEND_ORDER_FOR_DRAWING_APPROVAL'].includes(id),
                    onSubmit: (formData, otherParam) => confirmation(toStatusCode, id, formData, data.payload, otherParam),
                    resetFormData: getApprovalResetData(data.payload, id === 'MAKE_IN_PROGRESS'),
                    resetForm: true,
                  },
                  stage
                })
              );
              setLoading(false);
            }
          }
        );
        break;
      case 'UPLOAD_DOCUMENT':
        dispatch(getShipmentInfo({ id: original.shipment_id })).then((data) => {
          if (data && data.payload) {
            dispatch(
              openViewOrderDialog({
                completeOrderData: original,
                data: data.payload,
                param: {
                  name: 'Send for Approval',
                  type: 'approve',
                  title: name,
                  submitButtonName: name,
                  toStatusCode,
                  onSubmit: (formData, otherParam, approvalPayload) => confirmation(toStatusCode, id, formData, data.payload, otherParam, approvalPayload),
                  showPriceField: true,
                  isApproval: original.is_approval_type,
                  documentApproval: true
                },
                stage,
              }))
          }
        });
        break;
      case 'SEND_PRICE_APPROVAL_FOR_VAS':
      case 'MAKE_SHIPMENT':
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openViewOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name,
                    type: 'approve',
                    submitButtonName: name === "Review VAS Price" ? "Send Price Approval for VAS" : name,
                    showApproveButton: true,
                    title: name,
                    resetFormData: getApprovalResetData(data.payload, id === 'SEND_PRICE_APPROVAL_FOR_VAS'),
                    resetForm: true,
                    sendFormValue: id === 'SEND_PRICE_APPROVAL_FOR_VAS',
                    showPriceField: true,
                    onSubmit: (formData, code) => confirmation(toStatusCode, id, formData, data.payload, code)
                  },
                  stage
                })
              );
            }
          }
        );
        break;
      case 'REJECT_QUOTE':
        dispatch(getEcomWidgetsOrderItems({ [key]: original.id, stage })).then(
          (data) => {
            if (data && data.payload) {
              dispatch(
                openOrderDialog({
                  completeOrderData: original,
                  data: data.payload,
                  param: {
                    name: 'rejectQuote',
                    title: name,
                    successMessage: 'Your note has been sent successfully',
                    submitButtonName: name,
                    onSubmit: ({ note }) => {
                      confirmation(toStatusCode, id, { note })
                    }
                  },
                  stage
                })
              );
            }
          }
        );
        break;
      case 'REVERSE_PAYMENT':
        // Belt and braces with the menu filter above: the action should never reach a supplier
        // invoice, and if a stale menu or a config change gets it here it does nothing.
        if (type === 'supplier-invoices' || type === 'Supplier Invoice') {
          break;
        }
        // A row is an invoice and an invoice can carry several payments, so this opens the list to
        // choose from rather than guessing which one came back.
        setReversePaymentFor(original);
        break;
      case 'MAKE_PAYMENT':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'payment',
              title: 'Add Payment',
              successMessage: 'Success',
              maxWidth: 'md',
              isSupplierInvoice: type === 'supplier-invoices' || type === 'Supplier Invoice',
              loadOrdersFun: refreshTableData
            },
            stage,
          })
        );
        break;
      case 'MAKE_ADVANCE_PAYMENT':
        setLoading(true);
        dispatch(getAdvancePayment(original.id))
          .unwrap()
          .then((response) => {
            setLoading(false);
            dispatch(
              openOrderDialog({
                completeOrderData: original,
                data: { ...original, advance_payment_data: response },
                param: {
                  name: 'payment',
                  paymentType: 'advance',
                  title: 'Add Advance Payment',
                  successMessage: 'Success',
                  maxWidth: 'md',
                  isSupplierInvoice: type === 'supplier-invoices' || type === 'Supplier Invoice',
                },
                stage,
              })
            );
          });
        break;
      case 'VIEW_INVOICE':
        setLoading(true);
        dispatch(getBriefInvoiceDetails({ id: original.id }))
          .unwrap()
          .then((payloadData) => {
            dispatch(
              openOrderDialog({
                completeOrderData: original,
                data: { ...original, ...payloadData, id: original.id },
                param: {
                  name: 'invoice',
                  title: 'Invoice Details',
                  maxWidth: payloadData?.discrepancy?.warning_active ? 'md' : undefined,
                  isSupplierInvoice: type === 'supplier-invoices' || type === 'Supplier Invoice',
                },
                stage
              })
            );
          })
          .catch(() => {
            dispatchMessage(dispatch, 'Unable to load invoice details', 'error');
          })
          .finally(() => setLoading(false));
        break;
      case 'STATUS_CHANGE':
        dispatch(
          openOrderDialog({
            completeOrderData: original,
            data: original,
            param: {
              name: 'status',
              title: 'Update Status',
              loadOrdersFun: refreshTableData
            },
            stage
          })
        );
        break;
      case 'CREATE_DIRECT_SHIPMENT_SUPPLIER_ORDER':
        dispatch(
            openProductDialog({
              param: {
                name: 'supplierOrderDirectShipment',
                title: name,
                orderNo: original.id,
                stage,
                loadProducts: refreshTableData,
                action: name,
                editSupplierOrder: original.edit_supplier_order ?? true,
                // added !original.edit_supplier_order to not show confirmation dialog for back order
                // added !original.is_drawing_order condition to not open drawing required confirmation dialog again
                // showDrawingConfirmation: id === 'CREATE_SUPPLIER_ORDER' && !original.edit_supplier_order && !original.is_drawing_order,
                toStatusCode,
              },
            })
          );
        break;
      case 'CREATE_VAS_ORDER':
      case 'CREATE_SUPPLIER_ORDER':
        // We have two different popup in this case
        // Scenario 1. We are going to select Supplier in this popup
        // Scenario 2. Supplier is already selected, We are just going to change the supplier/data/proceed
        setLoading(true);
        // Based on showDialog API response, open the popup
        dispatch(showDialog({ order_id: original.id, dialog_name:"Inventory Dialog"}))
          .then((response) => {
            const showInventoryDialog = response?.payload?.inventory_dialog;
            setLoading(false);
            if(showInventoryDialog === true) {
              // Scenario 1. We are going to select Supplier in this popup
              dispatch(
                openProductDialog({
                  param: {
                    name: 'supplierOrderDirectShipment',
                    title: name,
                    orderNo: original.id,
                    stage,
                    loadProducts: refreshTableData,
                    action: name,
                    editSupplierOrder: original.edit_supplier_order ?? true,
                    // added !original.edit_supplier_order to not show confirmation dialog for back order
                    // added !original.is_drawing_order condition to not open drawing required confirmation dialog again
                    showDrawingConfirmation: id === 'CREATE_SUPPLIER_ORDER' && !original.edit_supplier_order && !original.is_drawing_order,
                    toStatusCode,
                  },
                })
              );
            }
            if(showInventoryDialog === false) {
              // Scenario 2. Supplier is already selected, We are just going to change the supplier/data/proceed
              dispatch(
                openChooseSupplierDialog({
                  param: original.id,
                  completeOrderData: original,
                  name:  'Create Supplier Order',
                  status: original.status.name,
                  stage,
                  loadOrdersFun: refreshTableData,
                  // added !original.is_drawing_order condition to not open drawing required confirmation dialog again
                  addOnParam : { isCreateSupplierOrder: true, showDrawingConfirmation: (id === 'CREATE_SUPPLIER_ORDER' && !original.is_drawing_order), toStatusCode},
                })
              );
            }
          })
        break;
      case 'EXPORT_SHIPMENT':
        downloadFile({ params: { id: row.original.shipment_id }, endPoint: '/api/exportShipment' })
          .then(() => dispatch(showMessage({ type: 'success', message: 'File downloaded successfully' })))
          .catch(() => dispatch(showMessage({ type: 'error', message: 'Failed to download' })))
        break;
      case 'EXPORT_SHIPMENT_WITH_PRICE':
        dispatch(openOrderDialog({
          completeOrderData: original,
          data: { id: original.shipment_id },
          param: {
            name: '',
            title: name,
            successMessage: 'Invoice has been generated',
            submitButtonName: name,
            loadOrdersFun: refreshTableData,
            hideDialogActions: true,
            additionalButton: additionalButton()
          },
          field: 'orderDialog',
          stage
        }))
        break;
      case 'EXPORT_INVOICE_CUSTOMER':
        downloadFile({ params: { id: row.original.id, }, endPoint: '/api/exportInvoice' })
          .then(() => dispatch(showMessage({ type: 'success', message: 'File downloaded successfully' })))
          .catch(() => dispatch(showMessage({ type: 'error', message: 'Failed to download' })))
        break;
      case 'COMPLETE_AND_SHIP':
        dispatch(openOrderDialog({
          completeOrderData: original,
          data: { id: original.shipment_id , ...original},
          param: {
            name: 'complete_shipment',
            title: name,
            successMessage: 'Completed And Shipped',
            submitButtonName: name,
            loadOrdersFun: refreshTableData,
            maxWidth: 'md'
          },
          stage
        }))
        break;
      case 'GENERATE_INVOICE':
        dispatch(openOrderDialog({
          completeOrderData: original,
          data: { id: original.shipment_id },
          param: {
            name: 'generate_invoice',
            title: name,
            successMessage: 'Invoice has been generated',
            submitButtonName: name,
            loadOrdersFun: refreshTableData,
          },
          field: 'orderDialog',
          stage
        }))
        break;
      case "REQUEST_VAS":
        showConfirmationDialog(dispatch, {
          title: name,
          message:
            "Do you want the order to be sent to the designer for VAS proofing?",
          displayCancelBtn: "yes",
          cancelActionName: "No",
          actionName: "Yes",
          onActionBtnClick: (selectionType) => {
            if(selectionType !== 'close') {
              dispatch(
                updateOrder({
                  id: original.id,
                  to_statuses_type_id: toStatusCode,
                  updateStatus: "yes",
                })
              ).then((res) => {
                if (!res.error) {
                  dispatch(
                    showMessage({
                      message:
                        "The order has been successfully sent to the designer for VAS proofing",
                      type: "success",
                    })
                  );
                  refreshTableData();
                }
              });
            }
          },
        });
        break;
      case 'LOCK_SHIPMENT':
        showConfirmationDialog(dispatch, {
          title: name,
          message: "Are you sure you want to lock the shipment?",
          displayCancelBtn: 'yes',
          actionName: 'Confirm',
          onActionBtnClick: (selectionType) => {
            if (selectionType !== "close") {
              const confirmParams = {
                id: original.shipment_id,
                to_statuses_type_code: toStatusCode
              }
              // PGSS-5794: once per container while the request runs; the backdrop blocks the screen.
              changeContainerStatusOnce(original.shipment_id, () =>
                dispatch(updateShipmentContainer(confirmParams)).then(res => {
                // checks for error from backend for locking if there are no orders in it error from BE comes only if there are no orders
                if (!res.error) {
                  if (_.has(res?.payload, "error")) {
                    if (res?.payload?.error === true) {
                      dispatch(showMessage({ message: "Please load orders to allow locking", type: 'error' }))
                      refreshTableData()
                    }
                  }else{
                    dispatch(showMessage({ message: "Shipment has been locked successfully", type: 'success' }))
                    refreshTableData()
                  }
                }else{
                    dispatch(showMessage({ message: "Error processing shipment", type: 'error' }))
                    refreshTableData()
                  }
                }), setLoading)
            }
            
          },
        })
        break;
      case 'UNLOCK_SHIPMENT':
        showConfirmationDialog(dispatch, {
          title: name,
          message: "Are you sure you want to unlock the shipment?",
          displayCancelBtn: 'yes',
          actionName: 'Confirm',
          onActionBtnClick: (selectionType) => {
            if (selectionType !== "close") {
              const confirmParams = {
                id: original.shipment_id,
                to_statuses_type_code: toStatusCode
              }
              // PGSS-5794: once per container while the request runs; the backdrop blocks the screen.
              changeContainerStatusOnce(original.shipment_id, () =>
                dispatch(updateShipmentContainer(confirmParams)).then(res => {
                  if (!res.error) {
                    dispatch(showMessage({ message: "Shipment has been unlocked successfully", type: 'success' }))
                    refreshTableData()
                  }else{
                    dispatch(showMessage({ message: "Error processing shipment", type: 'error' }))
                    refreshTableData()
                  }
                }), setLoading)
            }
          }
        })
        break;
      case 'SEND_SHIPMENT':
        showConfirmationDialog(dispatch, {
          title: name,
          message: "Are you sure you want to send the shipment for approval?",
          displayCancelBtn: 'yes',
          actionName: 'Confirm',
          onActionBtnClick: (selectionType) => {
            if (selectionType !== "close") {
              const confirmParams = {
                id: original.shipment_id,
                to_statuses_type_code: toStatusCode
              }
              // PGSS-5794: once per container while the request runs; the backdrop blocks the screen.
              changeContainerStatusOnce(original.shipment_id, () =>
                dispatch(updateShipmentContainer(confirmParams)).then(res => {
                  if (!res.error) {
                    dispatch(showMessage({ message: "Shipment has been sent for approval successfully", type: 'success' }))
                    refreshTableData()
                  }else{
                    dispatch(showMessage({ message: "Error processing shipment", type: 'error' }))
                    refreshTableData()
                  }
                }), setLoading)
            }
            
          }
        })
        break;
      case 'UNLOAD_ORDERS':
        if (user.role === 'supplier') {
          dispatch(viewShipment(original.shipment_id)).then(( response ) => {
            if (response?.payload?.shipment.length > 0) {
              navigate(`/container-management/unload/${original.shipment_id}`)
            } else {
              dispatch(showMessage({ message: "No orders exist to unload", type: 'error' }));
            }
          });
        }
        break;
      case "ADD_TO_CONTAINER":
        setLoading(true);
        dispatch(getOpenContainers({order_id: original?.id}))
        .then((response) => {
          setLoading(false);
          if(!response.error){
            const data = response?.payload?.containers
            dispatch(openOrderDialog({
            completeOrderData: original,
            data,
            param: {
              name: 'add_to_container',
              title: name,
              successMessage: 'Order assigned to container successfully',
              submitButtonName: name,
              loadOrdersFun: refreshTableData,
              maxWidth: 'lg',
              hideDialogActions: true,
            },
            stage
          }))
          }else{
            throw new Error( 'Failed to load');
         }
        }
        );
        break;
      
      case 'QUICK_EDIT_SHIPMENT':
        dispatch(openOrderDialog({
          completeOrderData: original,
          data: { id: original.shipment_id , ...original},
          param: {
            name: 'quick_edit_shipment',
            title: name,
            successMessage: 'Shipment Updated successfully',
            submitButtonName: "Save",
            loadOrdersFun: refreshTableData,
            maxWidth: 'md'
          },
          stage
        }))
        break;
      default:
        break;
    }
  };

  const pathFunction = (uiId) => {
    switch (uiId) {
      case 'CONVERT_TO_QUOTE':
        // TODO Need to clarify whether this is correct
        if (user.role === 'supplier') return `/rfqs/supplier-quotes/${original.id}`;
        if (['admin', 'staff', 'sales'].includes(user.role)) return `/rfqs/admin-quotes/${original.id}?type=convert-to-quote`
        return `/quotes/${original.id}`
      case 'CONVERT_TO_PROOF':
        return `/rfd/supplier-quotes/${original.id}`
      case 'VIEW_SUPPLIER_INVOICE':
        return `/supplier-invoices?show_suppier_invoice=true&stage=invoice&customer_invoice_id=${original.id}`
      case 'VIEW_ORDER':
        // PGSS-5369: the stage is not always the route. 'inproduction-orders' would build
        // `/inproduction-orderss/`; that list is orders, and /orders/:orderId exists.
        return `/${getActionRouteBase(stage) ?? `${stage}s`}/${original.id}?type=view`;
      case 'EDIT':
      case 'EDIT_RFQ':
      case 'EDIT_ORDER':
      case 'EDIT_QUOTE':
      case 'VIEW_QUOTE':
        return `/${type}/${original.id}`
      case 'RECEIVE_SHIPMENT':
        return `/shipments/receive/${original.shipment_id}`
      case 'PACKING_SLIP':
        return `/shipments/print-label/${original?.shipment_id}`
      case 'REVIEW_NOTE': {
        let title = ''
        let redirectionType = type
        if ((isCustomer(user.role) || isSupplier(user.role)) && !['rfq', 'quotes'].includes(type)) {
          if (stage === 'rfd') {
            title = 'Rfp'
            redirectionType = 'rfds'
          }
          if (stage === 'completed') {
            title = 'Rfp Completed'
            redirectionType = 'completed'
          }
          if (stage === 'order') {
            title = isCustomer(user.role) ? 'Orders' : 'Received Order'
            redirectionType = 'orders'
          }
          if (stage === 'confirmed') {
            title = 'Confirmed Order'
            redirectionType = 'confirmed'
          }
          // PGSS-5369: In Production -> Orders View. Its own stage for the action lookup, but the
          // rows are orders, so Send Message opens /orders/{id}?tab=6 -- a route that exists.
          if (stage === INPRODUCTION_ORDERS_STAGE) {
            title = 'Order'
            redirectionType = 'orders'
          }
          if (stage === 'rfq') {
            redirectionType = 'rfqs'
          }
        }
        if (type === 'rfqs') {
          if (isCustomer(user.role) || isSupplier(user.role)) {
            redirectionType = 'rfqs'
          } else {
            redirectionType = 'rfq'
          }
        }
        if (stage === 'order' && original?.status?.title === 'proof' && isCustomer(user.role)) {
          redirectionType = 'awaiting-approval'
        }
        return `/${redirectionType}/${original.id}?type=view&tab=6&title=${title}`
      }
      case 'SHIPMENT_COMMUNICATION': {
        return `/shipments/communication/${original.shipment_id}`
      }
      case 'VIEW_RFQ':
        if (cell.column.userManage) {
          return `/rfq/${original.id}`
        }
        return ''
      case 'REVISE_QUOTE':
        if (cell.column.userManage) {
          return `/${type}/${original.id}?type=revise-quote`
        }
        return ''
      // return cell.column.userManage || isCustomer(user.role) ? `/${type}/${original.id}` : '';
      case 'SHIPMENT_PRINT':
        return `/shipments/shippingLabel/${original.shipment_id}`;
      case 'PRINT_PRODUCTION':
        return `/shipments/printProduction/${original.shipment_id}`
      case 'EDIT_SHIPMENT':
        return `/shipments/${original.shipment_id}`
      case 'STATUS_UPDATE':
        if (stage === 'rfq' && cell.column.userManage) {
          return `/${type}/${original.id}?type=in-review`
        }
        return ''
      case 'SUBMIT_VAS_PROOF':
          return `/vas/${original.id}/proof`;
      case "VAS_PROOF_INPROGRESS":
        return `/vas/${original.id}/check-proof`;
      case "REQUEST_INFORMATION":
        return `/vas/${original.id}/check-proof`;          
      case 'REVIEW_PROOF':
        return `/vas/${original.id}/review`;
      case 'READY_FOR_PLOTTING':
      case 'IN_SANDBLASTING':
      case 'IN_LASER_ETCHING':
      case 'READY_SEND_FOR_VAS_APPROVAL':
        return `/vas/${original.id}/vas-production`
      case 'REVIEW_VAS':
        return `/vas/${original.id}/production-review`;
      case 'ATTACH_DRAWING':
        return `/drawing/${original.id}/request`;
      case 'UPLOAD_IMAGE':
        return `/shipments/uploadProductImages/${original.shipment_id}`;
      case 'APPROVE_PRODUCT_IMAGE':
        return `/shipments/reviewProductImages/${original.shipment_id}`; 
      case 'REVISE_PRODUCT_IMAGE':
        return `/shipments/reviewProductImages/${original.shipment_id}`;
      case 'EDIT_SUPPLIER_QUOTE':
        if (user.role === 'supplier') return `/rfqs/supplier-quotes/${original.id}`; 
        return '';
      case 'LOAD_ORDERS':
        if (user.role === 'supplier') return `/container-management/load/${original.shipment_id}`; 
        return '';
      default:
        return '';
    }
  };

  const getRoute = (path, uiId) => {
    if (cell.column.userManage && lowerCase(uiId).includes('view') && !['REVIEW_NOTE', 'VIEW_SUPPLIER_INVOICE', 'VIEW_ORDER', 'REVIEW_PROOF', 'REVIEW_VAS'].includes(uiId))
      return `${path}?type=view`
    return path
  }

  const menuObj = {
    handleClick,
    pathFunction,
    allowActions,
    getRoute,
    closeDialog,
    closeViewOrderDialog,
  }

  // PGSS-5453: this rule only ever runs in popup mode, so it is the popup's own filter.
  // It used to exempt a manage user from it entirely, which is how the View Details popup came to
  // offer "View Order" for the order it was already showing. A view action points back at the
  // record on screen no matter who is looking at it, so nobody gets it here.
  // `VIEW_${upperCase(getTitle)}` is this list's own default view action -- the same id
  // defaultAction is built with -- so the invoice list drops VIEW_INVOICE in its own popup while
  // an order keeps it.
  const checkShow = (uiId) => !isViewAction(uiId, `VIEW_${upperCase(getTitle)}`);


  const showActions = (id) => {
    switch (id) {
      case 'UPDATE_INVOICE':
        return type !== 'supplier-invoices' && ['DS', 'Direct Shipment', 'DP', 'Direct Pickup'].includes(original?.order_type);
      case 'REVIEW_INVOICE_DISCREPANCY':
        // PGSS-5306: reachable after Invoiced for every customer invoice order type (not DS/DP-only).
        return type !== 'supplier-invoices';
      case 'VIEW_SUPPLIER_INVOICE':
        return !original.is_inventory_invoice
      case 'GENERATE_INVOICE':
        return original.is_invoiced
      case 'SEND_FOR_PROOF_APPROVAL':
        return original.is_proof_approval
      case 'SEND_FOR_DRAWING_APPROVAL':
        return original.is_drawing_approval
      case 'REJECT_PROOF':
      case 'REQUEST_REVISION_FOR_PROOF':
      case 'APPROVE_PROOF':
      case 'APPROVE_VAS':
      case 'REQUEST_REVISION_FOR_VAS':
      case 'REJECT_VAS':
        return false;
      case 'APPROVE_VAS_PRICE':
      case 'APPROVE_DRAWING':
      case 'REQUEST_REVISION_FOR_VAS_PRICE':
      case 'REQUEST_REVISION_FOR_DRAWING':
      case 'REJECT':
      case 'REJECT_DRAWING':
        // PGSS-5453: a customer approves from a POPUP, never from the row -- `popupButtons` is
        // that test, and it is the same one APPROVE_DRAWING_ORDER below already uses.
        //
        // `approveProof` alone was not that test. It is only truthy when ViewOrderDialog's
        // `combineAction` fires, and that enumerates the dialog stage as ['order', 'quote'] --
        // but the customer's own approval list is /awaiting-approval, mounted as
        // `<Orders type="approval" />`, so its rows open the dialog under stage 'approval'. Same
        // orders, a different label, and every approval action fell out of the popup there:
        // Approve Order / Request Revision / Reject Order at Waiting-Approval, Approve Vas Price
        // and its pair at Waiting-Vas Price Approval, leaving the popup with Send Message and
        // Download. They were reachable from the dashboard, which renders its own inbox buttons,
        // and from the row's Review Order -- which is why it read as "missing in the popup only".
        return isCustomer(user.role) ? Boolean(approveProof) || popupButtons : true;
      case 'APPROVE_DRAWING_ORDER':
      case 'REQUEST_REVISION_FOR_DRAWING_ORDER':
      case 'REJECT_DRAWING_ORDER':
      case 'SEND_ORDER_FOR_DRAWING_APPROVAL':// Send supplier submitted drawing to customer approval.
        // Added SEND_ORDER_FOR_DRAWING_APPROVAL here so it will display only in the popup not in the table row
        return popupButtons;
      case 'UPLOAD_DOCUMENT':
        return original.is_approval_type
      case 'EXPORT_INVOICE_CUSTOMER':
        if (user.role === "customer") return true
        return false
      case 'REJECT_QUOTE':
      case 'STATUS_UPDATE':
        if (isCustomer(user.role)) {
          return approveProof
        }
        // PGSS-5453: `showRejectQuote`, not `!popupButtons`. A manage user does not approve,
        // reject or revise a quote from the row -- that is deliberate -- and does it from the
        // Review Quote popup, which is what `showRejectQuote` identifies. `popupButtons` is true
        // in EVERY popup, so the plain View Quote Details popup offered Approve Quote at
        // Revisions Requested while the table row beside it did not.
        if (cell.column.userManage && stage === "quote" && !showRejectQuote) {
          return false
        }
        return true
      case 'REQUEST_REVISION_FOR_QUOTE':
       if (isCustomer(user.role)) {
        return approveProof;
        }
       if (cell.column.userManage && stage === "quote" && !showRejectQuote) {
       return false;
       }
       return true;
      case 'CHOOSE_SUPPLIER':
        if (!checkNullOrUndefined(original.replied_quote_count) && Number(original.replied_quote_count <= 0)) {
          return false
        }
        return true
      case 'RFQ_REMINDER':
        return (
          !checkNullOrUndefined(original.replied_quote_count) &&
          !checkNullOrUndefined(original.sent_to_supplier_count) &&
          Number(original.sent_to_supplier_count) > 0 &&
          Number(original.replied_quote_count) < Number(original.sent_to_supplier_count)
        );
      case 'REVIEW_ORDER':
      case 'REVIEW_DRAWING':
      case 'REVIEW_DRAWING_ORDER':
        // Row only, and the pairing is the point: on the row these open the approval dialog, and
        // inside a popup the approval actions themselves are offered instead (see the customer
        // branch above, and APPROVE_DRAWING_ORDER below). Offering both would put two routes to
        // the same decision side by side.
        return !popupButtons;
      case 'APPROVAL_REMINDER': // Send Reminder Icon
      case 'VIEW_NOTES': // View Notes Icon
      case 'SHIPMENT_COMMUNICATION': // Communication Histories Icon
        // Added Condition to hide View Notes, Communication Histories and Send Reminder icon in the inbound shipment page
        return stage !== "shipment" || (stage === "shipment" && isOutboundShipments)
      case 'EDIT_SUPPLIER_QUOTE':
        return original.show_edit_quote_icon === true;
      default:
        return true;
    }
  }
  // PGSS-5453: built by fixed index below, so it carries `undefined` holes whenever the configured
  // set is shorter than those indices assume -- and every consumer destructures each entry.
  let rearrangedActions;


  if (actions) {
    // PGSS-5501: 'approval' is /awaiting-approval -- the same orders under another stage name. Left
    // to the catch-all below, the popup would move the first two actions (Approve, Request
    // Revision) to the end once those actions render through the approval branch.
    if (stage === 'order' || stage === 'approval') {
      if (isReviewActions) {
        rearrangedActions = [actions[1], actions[2], actions[3]]
      }
      else {
        rearrangedActions = actions;
      }
    } else if (stage === 'quote') {
      if (isCustomer(user.role)) {
        rearrangedActions = actions
      }
      else if (showRejectQuote) {
        // actions[4] has Reject Quote.
        rearrangedActions = [actions[1], actions[2], actions[3], actions[4]]
      }
      else if (popupButtons && cell.column.userManage) {
        rearrangedActions = [actions[1], actions[2], actions[3]]
      }
      else rearrangedActions = [...actions.slice(1), actions[0]];
    } else {
      rearrangedActions = [...actions.slice(2), ...actions.slice(0, 2)];
    }
    rearrangedActions = rearrangedActions.filter(Boolean);
  }

  const showDefaultAction = () => {
    switch (original?.status?.name) {
      case 'Confirmed':
        if (isCustomer(user.role)) {
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const getDetails = () => {
    let stageValue = { id: '', key: '', widgetId: '' };
    switch (stage) {
      case 'invoice':
        stageValue = {
          id: original.invoice_no,
          key: 'invoice_id',
          widgetId: original.id
        }
        break;
      case 'container':
      case 'in-production':
        stageValue = {
          id: original.container_number,
          key: 'shipment_id',
          widgetId: original.shipment_id
        }
        break;
      case 'shipment':
          stageValue = {
            id: original.shipment_no,
            key: 'shipment_id',
            widgetId: original.shipment_id
          }
          break;
      default:
        stageValue = {
          id: original.order_no,
          key: 'oid',
          widgetId: original.id
        }
    }

    return stageValue;
  };

  const getStageTitle = (isTooltipTitle) => {
    const stageData = stage
    switch (stageData) {
      case 'fulfilled':
      case 'confirmed':
      case 'order':
        return `View Order Details`;
      case 'quote':
        return `View Quote Details`;
      case 'rfq':
        return `View Rfq Details`;
      case 'invoice':
        return `View Invoice`;
      case 'approval':
        return `View Approval Items`;
      case 'shipment':
        return 'View Shipment';
      case 'container':
        return 'View Container Shipment'
      case 'in-production':
        return 'View In-production'
      default:
        return "";
    }
  }

  const handleViewOrder = () => {
    dispatch(getEcomWidgetsOrderItems({ [getDetails().key]: getDetails().widgetId, stage: stage === 'container' ? 'in-production' : stage, invoiceId: true })).then((data) => {
      if (data && data.payload) {
        // setOrderItems(data.payload);
        dispatch(
          openViewOrderDialog({
            completeOrderData: original,
            stage: stage === 'container' ? 'in-production' : stage,
            data: stage === 'invoice' ? data.payload.invoice : data.payload,
            param: {
              name: 'viewOrder',
              type: 'View',
              title: getStageTitle(false),
              showDialogActions: true,
              is_vas_order: original?.is_vas_order,
              packingSlips: data.payload?.packing_slips || [],
              supplierInvoices: data.payload?.supplier_invoices || [],
              isSupplierInvoice: type === 'supplier-invoices' || type === 'Supplier Invoice',
            },
          })
        );
      }
    })
  }

  const handleHideAction = (name) => {
    if (name === 'Revisions To Supplier' && original?.suppliers?.length === 0) return true
    return false
  }

  /**
   * Popup that is opened from Dialog button click(Popop inside another popup)
   * This function create additiona param that will be send to popup
   */
  const getAdditionParamForDialogButtons = (uiId) => {
    switch (uiId) {
      case 'REQUEST_REVISION_FOR_DRAWING_ORDER':{
          // If Admin/staff Request Revision for Drawing then pass note field as revision_note
          if(!isCustomer(user.role) && !isSupplier(user.role))
            return {
              showAutoNote: true, // Set this field for schema creation and display AutoNote field section in NoteFormCOntent
              noteFieldName: "revision_note", // Set this field for note field_name that will be sent to BE
              noteFieldLabelName: "Revision Comments", // Set this field for display Label name
            }
          return {};
        }
      default:
        return {};
    }
  }

  /**
   * PGSS-5453 — the popup's action set, derived from the row's.
   *
   * One predicate, applied to whichever list the branch below is rendering, so the popup drops
   * exactly what the row drops and nothing else. `handleHideAction` is part of it for every
   * branch: "Revisions To Supplier" on an order with no suppliers is wrong on both surfaces, and
   * applying it to only one of them is how the two drifted apart in the first place.
   */
  const isPopupAction = ({ name, ui_id: uiId }) =>
    !handleHideAction(name) && showActions(uiId) && checkShow(uiId);

  const popupDefaultActions = splitPopupActions(defaultAction, isPopupAction);
  const popupStatusActions = splitPopupActions(actions, isPopupAction);
  const popupApprovalActions = splitPopupActions(rearrangedActions, isPopupAction, maxCount);

  /**
   * The plain popup button: close the dialog, then run the action. Shared by the default-action
   * branch and the configured-action branch, which had drifted into two spellings of the same
   * markup -- one of which had been commented down to an empty fragment, leaving that branch with
   * no buttons at all while its overflow menu still rendered.
   */
  const renderPopupButton = ({
    name,
    icon,
    ui_id: uiId,
    to_statuses_types_code: toStatusCode,
    to_statuses_types_id: toStatusId,
  }) => {
    const asLink = pathFunction(uiId) && allowActions.includes(uiId);

    return (
      <Button
        key={uiId}
        className="mr-5"
        color="secondary"
        variant="contained"
        startIcon={
          <IconButton className="py-0 px-2" size="small">
            <Icon fontSize="small" sx={{ color: 'white' }}>{icon}</Icon>
          </IconButton>
        }
        component={asLink ? Link : undefined}
        to={getRoute(pathFunction(uiId), uiId)}
        state={
          asLink
            ? {
                name,
                ui_id: uiId,
                to_statuses_types_code: toStatusCode,
                to_statuses_types_id: toStatusId,
                status_actions: actions,
              }
            : undefined
        }
        onClick={() =>
          runPopupAction({
            name,
            ui_id: uiId,
            to_statuses_types_code: toStatusCode,
            to_statuses_types_id: toStatusId,
          })
        }
        sx={{ width: 'auto' }}
        disabled={popupActionRunning}
      >
        <span style={{ fontSize: 12 }}>{name}</span>
      </Button>
    );
  };

  /**
   * What an approval action does when the user picks it. Lifted out of the button's onClick so the
   * overflow menu can run it too: an action that does not fit inline must still behave like its
   * button, not fall back to the table's handler.
   */
  const handleApprovalAction = ({
    name,
    ui_id: uiId,
    to_statuses_types_code: toStatusCode,
    to_statuses_types_id: toStatusId,
  }) => {
    if (['Approve Quote', 'Send for Quote Approval'].includes(name)) {
      const orderDetails = buildSelectedDrawing(selectedImages, listData)
      confirmation(toStatusCode, uiId, { updateStatus: 'yes', order_details: orderDetails })
    } else if (name === 'Send Message') {
      // Updated code to not open the Send message popup. Instead of that it will open communication histories tab from Order page
      dispatch(closeViewOrderDialog());
      handleClick(uiId, toStatusCode, name, toStatusId);
    }
    else if (name === 'Download') {
      if(original?.order_no &&  original.id)
        dispatch(downloadOrderPdf(({ id: original.id, stage, orderNo: original?.order_no })))
    }
    else if (['SEND_ORDER_FOR_DRAWING_APPROVAL'].includes(uiId)) {
      // No need to show popup to get notes
      confirmation(toStatusCode, uiId)
    }
    else if (pathFunction(uiId) && allowActions.includes(uiId)) {
      // PGSS-5691: an action with its own page (Review VAS -> /vas/{id}/production-review, Review Proof ->
      // /vas/{id}/review) only goes there -- its button / menu item is a Link. It also opened the approval
      // note dialog on top of that page, and closing that dialog resets the order (OrderDialog, no
      // keepOrderData), so the page showed "Order #undefined" and No Data Found. Same as from the row.
      dispatch(closeViewOrderDialog());
    }
    else if (name !== 'Edit Quote') {
      dispatch(
        openOrderDialog({
          completeOrderData: original,
          data: original,
          param: {
            name: 'approveActions',
            title: name,
            successMessage: 'Success',
            submitButtonName: 'Submit',
            loadOrdersFun: refreshTableData,
            data: {
              uiId,
              toStatusCode,
              extraPayloadData: popupData?.extraPayloadData,
              passExtraPayload: popupData?.passExtraPayload
            },
            ...getAdditionParamForDialogButtons(uiId)
          },
          stage
        })
      );
    }
  };

  /**
   * PGSS-5470 — what a plain View Details popup button (or its overflow entry) does.
   *
   * By default: close the popup and run the row's handler. For the drawing decisions that handler
   * is the wrong one here -- on the row it opens a review of the order with a note box, so from
   * inside the popup the user saw the order details a second time and no document upload. The
   * Review Drawing popup already sends these to the approval dialog; this does the same from the
   * plain popup, which stays open underneath exactly as it does there.
   *
   * Everything else keeps the row's handler, including the actions PGSS-5471 decided keep their
   * review step. `opensApprovalDialogFromPopup` explains why the list is ids, and why so few.
   */
  const runPopupAction = (action) => {
    if (opensApprovalDialogFromPopup(action.ui_id)) {
      handleApprovalAction(action);
      return;
    }
    const firstClick = firstClickPopupAction(action, {
      stage,
      statusCode: original?.status?.system_code,
      isCustomerUser: isCustomer(user.role),
    });
    if (firstClick) {
      runFirstClickPopupAction(action, firstClick);
      return;
    }
    dispatch(closeViewOrderDialog());
    handleClick(action.ui_id, action.to_statuses_types_code, action.name, action.to_statuses_types_id);
  };

  /**
   * PGSS-5471 — run the action straight from View Details, with what its second view would have
   * collected: the ticked drawings, or the documents added to each line. `firstClickPopupAction`
   * lists which actions, and why they are keyed by status. The payloads are the ones the second
   * views sent, so the API sees the same request either way.
   */
  const runFirstClickPopupAction = (
    { ui_id: uiId, to_statuses_types_code: toStatusCode },
    { input },
  ) => {
    if (popupActionRunning) return;
    let payload = {};
    if (input === 'drawings') {
      payload = { updateStatus: 'yes', order_details: buildSelectedDrawing(selectedImages, listData) };
    } else if (input === 'documents') {
      payload = buildInlineDocumentsPayload(listData, methods?.getValues?.());
    }
    setPopupActionRunning(true);
    Promise.resolve(confirmation(toStatusCode, uiId, payload, listData)).finally(() =>
      setPopupActionRunning(false),
    );
  };

  const renderApprovalButton = (action) => {
    const { name, icon, ui_id: uiId } = action;

    return (
      <Button
        key={uiId}
        className='mr-5'
        color="secondary"
        variant="contained"
        startIcon={
          <IconButton className="py-0 px-2" size="small">
            <Icon fontSize="small" sx={{ color: 'white' }}>{icon}</Icon>
          </IconButton>
        }
        component={pathFunction(uiId) && allowActions.includes(uiId) ? Link : undefined}
        to={getRoute(pathFunction(uiId), uiId)}
        onClick={() => handleApprovalAction(action)}
        sx={{ width: 'auto' }}
        disabled={isSubmitted}
      >
        <span style={{ fontSize: 12 }}>{name}</span>
      </Button>
    );
  };

  return (
    <div
      onClick={onCellClick}
      onKeyDown={onCellClick}
      role="button"
      tabIndex="0"
      style={{ cursor: 'default', pointerEvents: 'auto' }}
      className="flex flex-row"
    >
      {/* PGSS-5551: rendered alongside View rather than through the configured-actions branch
          below, which is suppressed for archived rows. `actions` is deliberately left cleared:
          View Order comes only from the `!actions` default branch, so filling `actions` for
          archived invoices would take View away -- the empty-row failure PGSS-5446 describes.
          Staff only; a customer or supplier never sees it. */}
      {/* PGSS-5623: rendered on its own, like the reversal below, so it never touches the configured-actions
          branch or the `!actions` View default. */}
      <ProductImagesLink
        to={productImagesPath({
          pathname: location.pathname,
          role: user.role,
          shipmentId: original?.shipment_id,
          // PGSS-5637: container Waiting for Product Image Approval only (not order Pending,
          // not container Pending). Name covers both spellings if system_code is absent.
          statusCode: original?.status?.system_code,
          statusName: original?.status?.name,
        })}
      />
      {archivedInvoiceReversal && !isCustomer(user.role) && !isSupplier(user.role) && (
        <Tooltip title={archivedInvoiceReversal.name || 'Reverse Payment'}>
          <IconButton
            className="py-0 px-2"
            size="small"
            onClick={() => handleClick(
              archivedInvoiceReversal.ui_id,
              archivedInvoiceReversal.to_statuses_types_code,
              archivedInvoiceReversal.name,
              archivedInvoiceReversal.to_statuses_types_id,
            )}
          >
            <Icon fontSize="small">{archivedInvoiceReversal.icon || 'undo'}</Icon>
          </IconButton>
        </Tooltip>
      )}
      {/* PGSS-5367: fulfilled used to hard-exclude defaults, so statuses with no workflow_actions
          row (e.g. Pending For Receive Shipment) rendered an empty Actions cell on Shipped Orders. */}
      {!actions && (stage !== "fulfilled" || isSupplierShippedOrdersView) && showDefaultAction() && !isCustomer(user.role) && (!isSupplier(user.role) || isSupplierOrdersView) && <>
        {popupButtons ? (
          <>
            {popupDefaultActions.inline.map(renderPopupButton)}
            {popupDefaultActions.overflow.length > 0 && (
              <MenuActions actions={popupDefaultActions.overflow} obj={menuObj} />
            )}
          </>
        ) : (
          defaultAction.map(({ name, icon, ui_id: uiId, to_statuses_types_code: toStatusCode, to_statuses_types_id: toStatusId }) => {
            if (!showActions(uiId)) return null;
            return (
              <Tooltip key={uiId} title={name}>
                <IconButton
                  className="py-0 px-2"
                  size="small"
                  component={pathFunction(uiId) && allowActions.includes(uiId) ? Link : undefined}
                  to={getRoute(pathFunction(uiId), uiId)}
                  onClick={() => handleClick(uiId, toStatusCode, name, toStatusId)}
                >
                  <Icon fontSize="small">{icon}</Icon>
                </IconButton>
              </Tooltip>

            );
          })
        )}
      </>}
      {/* PGSS-5446: parsed, not substring-matched -- the old check needed a leading '&', so it
          missed Customer Archived, which puts archive first in the URL. */}
      {!isArchivedView(locationSearchKey) &&
        <div className="flex items-center">
          {/* PGSS-5453: the popup half of this branch rendered an empty fragment -- the buttons
              had been commented out -- while the overflow menu below it still ran off the same
              counter. The popup therefore showed none of the first three actions and only the
              tail of the raw list, which is the mismatch the ticket describes from both ends. */}
          {actions && approveProof === undefined && (
            popupButtons ? (
              <>
                {popupStatusActions.inline.map(renderPopupButton)}
                {popupStatusActions.overflow.length > 0 && (
                  <MenuActions actions={popupStatusActions.overflow} obj={{ ...menuObj, onSelect: runPopupAction }} />
                )}
              </>
            ) : (
              actions.map(({ name, icon, ui_id: uiId, to_statuses_types_code: toStatusCode, to_statuses_types_id: toStatusId }) => {
                if (handleHideAction(name)) return null
                if (!showActions(uiId)) return null;
                return (
                  <Tooltip key={uiId} title={name}>
                    <IconButton
                      className="py-0 px-2"
                      size="small"
                      component={pathFunction(uiId) && allowActions.includes(uiId) ? Link : undefined}
                      to={getRoute(pathFunction(uiId), uiId)}
                      state={{name, ui_id: uiId, to_statuses_types_code: toStatusCode, to_statuses_types_id: toStatusId, status_actions: actions}}
                      onClick={() => handleClick(uiId, toStatusCode, name, toStatusId)}
                    >
                      <Icon fontSize="small">{icon}</Icon>
                    </IconButton>
                  </Tooltip>

                );
              })
            )
          )}

          {
            // PGSS-5469: row only. It opens the View Details popup, so inside that popup it re-opened
            // the popup already on screen and appeared to do nothing.
            !popupButtons && actions && approveProof === undefined && original?.is_show_drawing_icon && (
              <Tooltip title="View Attached Diagram">
                <IconButton
                  className="py-0 px-2"
                  size="small"
                  onClick={() => handleViewOrder()}
                >
                  <Icon fontSize="small">attach_file</Icon>
                </IconButton>
              </Tooltip>
            )
          }

          {/* Portal: on the staff lists this renders inside the pinned (position: sticky) Actions
              cell, and a sticky cell is its own stacking context. Left there, the full-screen
              backdrop only covered the page up to that cell, and every later row's pinned
              checkbox / Order no / Actions cells painted above it, looking highlighted. */}
          {loading && (
            <Portal>
              <Backdrop open={loading} style={{ zIndex: 1301 }}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </Portal>
          )}

          {actions && approveProof && popupButtons &&
            popupApprovalActions.inline.map(renderApprovalButton)}

          {/* PGSS-5453: the remainder of the SAME filtered list, not `actions.slice(3)`. Indexing
              the raw set here is what let the menu offer actions the status filter had already
              rejected, and drop ones the inline cap had pushed out.
              `onSelect` so an action that overflows still does what its button would have done --
              the menu's own handler is the table's, which is a different dialog. */}
          {actions && approveProof && popupButtons && popupApprovalActions.overflow.length > 0 && (
            <MenuActions
              actions={popupApprovalActions.overflow}
              obj={{ ...menuObj, onSelect: handleApprovalAction }}
            />
          )}


          {/* Temporarily hidden this action */}
          {/* {cell.column.isManage && (
          <>
            {original.payment_status !== 'paid' && (
              <IconButton
                className="p-0"
                onClick={(ev) => {
                  ev.stopPropagation();
                  dispatch(
                    openOrderDialog({
                      completeOrderData: values,
                      data: original,
                      param: { name: 'payment', title: 'Add Payment', loadOrders: 'yes' },
                    })
                  );
                }}
                size="medium"
              >
                <Icon fontSize="small">attach_money</Icon>
              </IconButton>
            )}
          </>
        )} */}


        </div>
      }
      <Dialog
        open={!!reversePaymentFor}
        onClose={() => setReversePaymentFor(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {`Reverse Payment${reversePaymentFor?.invoice_number ? ` — ${reversePaymentFor.invoice_number}` : ''}`}
        </DialogTitle>
        <DialogContent>
          <InvoicePaymentsPanel
            invoiceId={reversePaymentFor?.id}
            canReverse={!!user && FuseUtils.hasPermission(authPermissions.paymentReversal, user.role)}
            onReversed={() => refreshTableData?.()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReversePaymentFor(null)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
}

export default OrdersRowActions;
